import type { PriceTableRow, UpsertRowBody, ReplaceRowsBody } from "./price-tables";

/** Editable draft: a server row minus recId/sortOrder/rowVersion (position = sortOrder). */
export interface PriceTableRowDraft {
  rateType: string | null;
  rangeFrom: number;
  rangeTo: number;
  rate1: number | null; rate2: number | null; rate3: number | null;
  rate4: number | null; rate5: number | null; rate6: number | null;
  rate7: number | null; rate8: number | null; rate9: number | null;
  rangeDesc: string | null;
}

export function blankRow(): PriceTableRowDraft {
  return { rateType: null, rangeFrom: 0, rangeTo: 0, rate1: null, rate2: null, rate3: null, rate4: null, rate5: null, rate6: null, rate7: null, rate8: null, rate9: null, rangeDesc: null };
}

export function rowError(row: PriceTableRowDraft): string | null {
  if (row.rangeFrom > row.rangeTo) return "From must be ≤ To";
  if (row.rangeFrom < 0 || row.rangeTo < 0) return "Range must be ≥ 0";
  return null;
}

/** Advisory only (overlap is allowed for parity) — returns a message or null. */
export function overlapWarning(rows: PriceTableRowDraft[]): string | null {
  const sorted = [...rows].map((r, i) => ({ r, i })).sort((a, b) => a.r.rangeFrom - b.r.rangeFrom);
  for (let k = 1; k < sorted.length; k++) {
    const prev = sorted[k - 1];
    const cur = sorted[k];
    if (prev && cur && cur.r.rangeFrom <= prev.r.rangeTo) return "Some quantity ranges overlap — pricing uses the first matching row.";
  }
  return null;
}

const RATE_KEYS = ["rate1", "rate2", "rate3", "rate4", "rate5", "rate6", "rate7", "rate8", "rate9"] as const;

function num(cell: string | undefined): number | null {
  if (cell === undefined || cell.trim() === "") return null;
  const n = Number(cell.trim());
  return Number.isFinite(n) ? n : NaN;
}

/** Parse pasted TSV/CSV. Column order: RateType, From, To, Rate1..Rate9, RangeDesc. Blank lines skipped. */
export function parsePastedRows(text: string): { rows: PriceTableRowDraft[]; errors: string[] } {
  const rows: PriceTableRowDraft[] = [];
  const errors: string[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  lines.forEach((line, idx) => {
    const cells = line.includes("\t") ? line.split("\t") : line.split(",");
    const from = num(cells[1]);
    const to = num(cells[2]);
    if (from === null || Number.isNaN(from) || to === null || Number.isNaN(to)) {
      errors.push(`Line ${idx + 1}: From/To must be numbers`);
      return;
    }
    const draft = blankRow();
    draft.rateType = cells[0]?.trim() || null;
    draft.rangeFrom = from;
    draft.rangeTo = to;
    RATE_KEYS.forEach((k, i) => {
      const v = num(cells[3 + i]);
      if (Number.isNaN(v)) errors.push(`Line ${idx + 1}: ${k} is not a number`);
      else draft[k] = v;
    });
    draft.rangeDesc = cells[12]?.trim() || null;
    rows.push(draft);
  });
  return { rows, errors };
}

export function toReplaceBody(rows: PriceTableRowDraft[]): ReplaceRowsBody {
  return { rows: rows.map((r, i): UpsertRowBody => ({ ...r, sortOrder: i })) };
}

export function fromDetailRows(rows: PriceTableRow[]): PriceTableRowDraft[] {
  return rows.map((r) => ({
    rateType: r.rateType, rangeFrom: r.rangeFrom, rangeTo: r.rangeTo,
    rate1: r.rate1, rate2: r.rate2, rate3: r.rate3, rate4: r.rate4, rate5: r.rate5,
    rate6: r.rate6, rate7: r.rate7, rate8: r.rate8, rate9: r.rate9, rangeDesc: r.rangeDesc,
  }));
}
